import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { isFirstStepSkipped } from '@/core/utils/rapidstor';

import classes from './styles.module.scss';

import Seperator from '../../../assets/journey-bar-seperator.svg?react';
import JourneyStep from './step';

type StepsType = 'regular' | 'reservation-with-fee';

interface Props {
    mobile?: boolean;
    desktop?: boolean;
    className?: string;
    // Optional override props for testing purposes only
    step?: number;
    stepsType?: StepsType;
}

/**
 * Derives the step number and steps type from the current route pathname
 */
function getStepFromRoute(pathname: string): {
    step: number;
    stepsType: StepsType;
} {
    // Check if we're on reservation-with-fee route
    if (pathname.includes('/reservation-with-fee')) {
        const isStep2 = pathname.includes('/return');
        return {
            step: isStep2 ? 2 : 1,
            stepsType: 'reservation-with-fee',
        };
    }

    // Regular journey steps
    // Step 1: index/root route
    if (pathname === '/' || pathname === '') {
        return { step: 1, stepsType: 'regular' };
    }

    // Step 2: unit-listing
    if (pathname.includes('/unit-listing') || pathname === '/unit-listing') {
        return { step: 2, stepsType: 'regular' };
    }

    // Step 3: book-unit or book-unit-map
    if (pathname.includes('/book-unit')) {
        return { step: 3, stepsType: 'regular' };
    }

    // Step 3 (mobile) or Step 4 (desktop): customise-booking
    if (
        pathname.includes('/customise-booking')
    || pathname === '/customise-booking'
    ) {
        return { step: 3, stepsType: 'regular' };
    }

    // Default to step 1
    return { step: 1, stepsType: 'regular' };
}

function JourneyProgress({
    mobile,
    desktop,
    className: classNameProp,
    step: stepProp,
    stepsType: stepsTypeProp,
}: Props) {
    const location = useLocation();
    const className = [
        classes.journeyBar,
        mobile ? classes.mobile : '',
        desktop ? classes.desktop : '',
        classNameProp,
    ].join(' ');

    const { t } = useTranslation();

    const firstStepSkipped = useMemo(() => isFirstStepSkipped(), []);

    // Derive step and stepsType from the current route (or use override props for testing)
    const { step, stepsType } = useMemo(() => {
        if (stepProp !== undefined && stepsTypeProp !== undefined) {
            return { step: stepProp, stepsType: stepsTypeProp };
        }
        return getStepFromRoute(location.pathname);
    }, [location.pathname, stepProp, stepsTypeProp]);

    const journeySteps = useMemo(() => {
        if (stepsType === 'regular') {
            // If first step is skipped, adjust step numbers
            if (firstStepSkipped) {
                // Step 1 (Personal & Business) is skipped, so:
                // - Step 2 (Storage Size) becomes step 1
                // - Step 3 (Pricing) becomes step 2
                // - Step 4 (if desktop) becomes step 3
                const actualStep1 = 2; // Storage Size
                const actualStep2 = 3; // Pricing
                const actualStep3 = 4; // Additional step (desktop only)

                return (
                    <>
                        <JourneyStep progress={1} step={step} actualStep={actualStep1}>
                            {t('journey_bar:step_2')}
                        </JourneyStep>
                        <Seperator />
                        <JourneyStep progress={2} step={step} actualStep={actualStep2}>
                            {desktop
                                ? t('journey_bar:step_3_a')
                                : t('journey_bar:step_3_b')}
                        </JourneyStep>
                        {desktop && step >= 3 && <Seperator />}
                        {desktop && step >= 3 && (
                            <JourneyStep progress={3} step={step} actualStep={actualStep3}>
                                {t('journey_bar:step_4')}
                            </JourneyStep>
                        )}
                    </>
                );
            }
            // Normal flow - first step is shown
            return (
                <>
                    <JourneyStep progress={1} step={step} actualStep={1}>
                        {t('journey_bar:step_1')}
                    </JourneyStep>
                    <Seperator />
                    <JourneyStep progress={2} step={step} actualStep={2}>
                        {t('journey_bar:step_2')}
                    </JourneyStep>
                    <Seperator />
                    <JourneyStep progress={3} step={step} actualStep={3}>
                        {desktop
                            ? t('journey_bar:step_3_a')
                            : t('journey_bar:step_3_b')}
                    </JourneyStep>
                    {desktop && step >= 3 && <Seperator />}
                    {desktop && step >= 3 && (
                        <JourneyStep progress={4} step={step} actualStep={4}>
                            {t('journey_bar:step_4')}
                        </JourneyStep>
                    )}
                </>
            );
        }
        return (
            <>
                <JourneyStep progress={1} step={step} actualStep={1}>
                    {t('journey_bar:step_5')}
                </JourneyStep>
                <Seperator />
                <JourneyStep progress={2} step={step} actualStep={2}>
                    {t('journey_bar:step_6')}
                </JourneyStep>
            </>
        );
    }, [step, stepsType, desktop, firstStepSkipped, t]);

    return <div data-testid="journey-progress" className={className}>{journeySteps}</div>;
}

export default JourneyProgress;
